// Copyright (c) 2026, Dokos SAS and Contributors
// License: GNU General Public License v3. See license.txt
// @dokos: Log Time mode of the Project Management view, weekly grid layout.
// One row per (project, task, activity type) of the employee's week: planned minutes
// (Task Assignments) as a striped badge, logged minutes (Timesheet Details) as a typed
// input. Edits are batched through `save_week` (debounced); the week is read with ONE
// `get_week` request. Under 768px the grid renders one day at a time (day chips + steppers).
// Plain DOM (no FullCalendar); same contract as the calendar classes of the view:
// make / destroy / refresh / setupLegend and a `fullcalendar` shim for the current date.

import { PMTimeLogDialog } from "./PMTimeLogDialog";
import {
	activityColor,
	formatDay,
	formatDayRange,
	getActivityTypeColors,
	h,
	scrollIntoStrip,
} from "./PMRender";
import {
	MOBILE_WIDTH,
	formatDuration,
	getCurrentEmployee,
	getOverriddenEmployee,
	parseDuration,
	viewAsAnotherEmployee,
} from "./PMUtils";

const API = "erpnext.projects.project_management";
const SAVE_DELAY = 800;
const STEP = 15;
const MAX_MINUTES = 24 * 60;
/** @dokos: roles allowed to open another employee's week (WEEK_MANAGER_ROLES server side). */
export const WEEK_MANAGER_ROLES = [
	"Projects Manager",
	"Projects User",
	"HR Manager",
	"HR User",
	"System Manager",
];
// @dokos: timesheet week status → status-map theme (Draft was red: a draft is not an error)
const STATUS_THEME = { Empty: "gray", Draft: "gray", Submitted: "blue", Mixed: "amber" };

const cellKey = (row, i) => `${row.key}@${i}`;
const sum = (values) => values.reduce((a, b) => a + (Number(b) || 0), 0);
const today = () => frappe.datetime.get_today();
const cellText = (minutes) => (minutes ? formatDuration(minutes) : "");

/** @dokos: ISO days of the week holding `date` (first day of week of the System Settings). */
export function weekDays(date) {
	const first = frappe.datetime.get_first_day_of_the_week_index();
	const start = moment(date, "YYYY-MM-DD");
	start.subtract((start.day() - first + 7) % 7, "days");
	return Array.from({ length: 7 }, (_, i) => start.clone().add(i, "days").format("YYYY-MM-DD"));
}

/** @dokos: "21–27 sept. 2026" / "28 sept. – 4 oct. 2026" (user language). */
export function weekLabel(days) {
	return formatDayRange(days[0], days[days.length - 1]);
}

const isWeekend = (day) => moment(day, "YYYY-MM-DD").isoWeekday() >= 6;

/** @dokos: an empty local row (not saved until one of its cells gets a value). */
function emptyRow({ project, project_name, task, task_subject, activity_type }) {
	return {
		key: [project || "", task || "", activity_type || ""].join("|"),
		project: project || null,
		project_name: project_name || project || null,
		task: task || null,
		task_subject: task_subject || task || null,
		activity_type: activity_type || null,
		planned: Array(7).fill(0),
		logged: Array(7).fill(0),
		details: Array.from({ length: 7 }, () => []),
		docstatus: Array(7).fill(null),
		local: true,
	};
}

/** @dokos */
export class PMViewGrid_Timelog {
	/**
	 * @param {Object} opts
	 * @param {unknown} opts.page
	 * @param {import("./ProjectManagementView.js").ProjectManagementView} opts.view
	 * @param {HTMLElement} opts.parent
	 */
	constructor({ page, view, parent }) {
		this.page = page;
		this.view = view;
		this.parent = $(parent).get(0);
		this.anchor = view?._date || today();
		this.employee = null;
		this.payload = null;
		this.rows = [];
		/** @type {Map<string, Object>} local rows added with "Add a row" */
		this.extraRows = new Map();
		/** @type {Map<string, {row: Object, i: number, minutes: number}>} edited, not saved yet */
		this.dirty = new Map();
		/** @type {Map<string, string>} cells holding an unparsable text (and that text) */
		this.invalid = new Map();
		this.refs = this.emptyRefs();
		this.colors = {};
		this.selectedDay = null;
		this.isMobile = false;
		this.saving = null;
		this.saveTimer = null;
		this.loadId = 0;
		this.actionsSignature = null;

		// @dokos: the view reads/moves the displayed date through `fullcalendar`
		this.fullcalendar = {
			getDate: () => moment(this.anchor, "YYYY-MM-DD").toDate(),
			gotoDate: (date) => this.goto(moment(date).format("YYYY-MM-DD")),
		};
	}

	emptyRefs() {
		return { cells: new Map(), rowTotals: new Map(), dayTotals: [], chips: [], week: null };
	}

	// -------------------------------------------------------------------------------
	// Contract of the view
	// -------------------------------------------------------------------------------

	async make() {
		this.root = h("div", { class: "pm-log" });
		this.parent.appendChild(this.root);
		this.isMobile = this.root.getBoundingClientRect().width < MOBILE_WIDTH;
		this.resizeObserver = new ResizeObserver((entries) => {
			const width = entries[0]?.contentRect.width;
			if (!width) return;
			const mobile = width < MOBILE_WIDTH;
			if (mobile !== this.isMobile) {
				this.isMobile = mobile;
				this.render();
			}
		});
		this.resizeObserver.observe(this.root);

		// @dokos: the week is requested with the employee and the colours, not after them
		// (get_week defaults to the session user's employee): one round trip, not two
		this.render();
		const lookups = Promise.all([getCurrentEmployee(), getActivityTypeColors()]).then(
			([employee, colors]) => {
				if (this.destroyed) return;
				this.employee ||= employee || null;
				this.colors = colors || {};
				if (this.payload) this.render();
			}
		);
		await Promise.all([lookups, this.load()]);
	}

	destroy() {
		this.destroyed = true;
		clearTimeout(this.saveTimer);
		if (this.dirty.size) this.flush(); // don't lose the last edits when switching mode
		this.resizeObserver?.disconnect();
		this.employeeControl = null;
		this.root?.remove();
		this.root = null;
	}

	refresh() {
		return this.load();
	}

	setupLegend(container) {
		const item = (cls, label) =>
			h(
				"span",
				{ class: "pm-legend-item" },
				// @dokos: dk-swatch size, the cell-state look (stripes / outline / fill)
				h("span", {
					class: ["dk-swatch pm-log-legend-swatch", cls],
					attrs: { "aria-hidden": "true" },
				}),
				h("span", { class: "legend-label", text: label })
			);
		container.append(
			item("is-planned", __("Assigned")),
			item("is-draft", __("Draft")),
			item("is-submitted", __("Submitted"))
		);
	}

	/** @dokos: page buttons of the grid (rendered by the view, see setup_pm_actions). */
	getPageActions() {
		const status = this.payload?.status;
		const actions = [];
		if (this.canWrite() && status !== "Submitted" && this.payload) {
			actions.push({
				label: __("Fill from assignments", null, "PM View"),
				action: () => this.fillFromAssignments(),
			});
		}
		if (this.canSubmit()) {
			actions.push({ label: __("Submit Timesheet"), action: () => this.submitWeek() });
		}
		if (this.canCancel()) {
			actions.push({ label: __("Cancel Timesheet"), action: () => this.cancelWeek() });
		}
		return actions;
	}

	/** @dokos: primary "Add Entry": free-form time log with a start time. */
	openTimeLogDialog() {
		const date = this.days().includes(this.anchor) ? this.anchor : this.days()[0];
		return new PMTimeLogDialog({ date, employee: this.payload?.employee || this.employee }, () =>
			this.load()
		).show();
	}

	// -------------------------------------------------------------------------------
	// Permissions
	// -------------------------------------------------------------------------------

	isManager() {
		return WEEK_MANAGER_ROLES.some((role) => frappe.user.has_role(role));
	}

	canWrite() {
		return frappe.model.can_write("Timesheet") || frappe.model.can_create("Timesheet");
	}

	canSubmit() {
		return ["Draft", "Mixed"].includes(this.payload?.status) && frappe.model.can_submit("Timesheet");
	}

	canCancel() {
		return ["Submitted", "Mixed"].includes(this.payload?.status) && frappe.model.can_cancel("Timesheet");
	}

	isLocked(row, i) {
		return !this.canWrite() || this.payload?.status === "Submitted" || row.docstatus?.[i] === 1;
	}

	// -------------------------------------------------------------------------------
	// Data
	// -------------------------------------------------------------------------------

	days() {
		return this.payload?.days || weekDays(this.anchor);
	}

	async load() {
		const id = ++this.loadId;
		try {
			const payload = await frappe.xcall(`${API}.get_week`, {
				employee: this.employee || getOverriddenEmployee() || null, // @dokos
				week_start: this.anchor,
			});
			if (id !== this.loadId || this.destroyed) return;
			this.applyPayload(payload);
		} catch (e) {
			if (id !== this.loadId || this.destroyed) return;
			this.payload = null;
			this.error = true;
			this.render();
		}
	}

	/**
	 * Show a `get_week()` payload of the displayed week. Rows shown before and missing
	 * from it (emptied by the user) are kept as local rows until the week changes.
	 * @param {Object} payload
	 */
	applyPayload(payload) {
		this.error = false;
		const previous = this.rows;
		this.payload = payload;
		if (!this.employee) this.employee = payload.employee;

		const keys = new Set(payload.rows.map((r) => r.key));
		// keep the rows the user was working on, even once emptied
		for (const row of previous) {
			if (!keys.has(row.key) && !this.extraRows.has(row.key)) {
				this.extraRows.set(row.key, emptyRow(row));
			}
		}
		for (const key of [...this.extraRows.keys()]) {
			if (keys.has(key)) this.extraRows.delete(key);
		}
		const rows = [...payload.rows, ...this.extraRows.values()];
		const sameRows = rows.length === previous.length && rows.every((r, i) => r.key === previous[i].key);
		this.rows = rows;

		if (sameRows && this.refs.cells.size) {
			this.patch();
		} else {
			this.render();
		}
		this.syncPageActions();
	}

	/** Minutes shown in a cell: the edited value if any, else the saved one. */
	effective(row, i) {
		const dirty = this.dirty.get(cellKey(row, i));
		return dirty ? dirty.minutes : row.logged[i] || 0;
	}

	/** Re-render the page buttons only when they change (status, permissions). */
	syncPageActions() {
		const signature = this.getPageActions()
			.map((a) => a.label)
			.join("|");
		if (signature !== this.actionsSignature) {
			this.actionsSignature = signature;
			this.view?.setup_pm_actions?.();
		}
	}

	// -------------------------------------------------------------------------------
	// Saving
	// -------------------------------------------------------------------------------

	scheduleSave() {
		clearTimeout(this.saveTimer);
		this.saveTimer = setTimeout(() => this.flush(), SAVE_DELAY);
	}

	/** Save every edited cell in one `save_week` call; chained while a save is running. */
	async flush() {
		clearTimeout(this.saveTimer);
		while (this.saving) await this.saving;
		if (!this.dirty.size || !this.payload) return;

		const days = this.days();
		const sent = [...this.dirty.values()];
		const cells = sent.map(({ row, i, minutes }) => ({
			project: row.project,
			task: row.task,
			activity_type: row.activity_type,
			date: days[i],
			minutes,
		}));
		for (const { row, i } of sent) this.dirty.delete(cellKey(row, i));

		const args = { employee: this.payload.employee, week_start: this.payload.week_start, cells };
		this.setBusy(true);
		this.saving = frappe
			.xcall(`${API}.save_week`, args)
			.then((payload) => {
				if (!this.destroyed && payload.week_start === this.payload?.week_start) {
					this.applyPayload(payload);
				}
			})
			.catch(() => {
				if (this.destroyed) return;
				frappe.show_alert({
					message: __("The time logs could not be saved", null, "PM View"),
					indicator: "red",
				});
				this.dirty.clear();
				this.invalid.clear();
				return this.load();
			})
			.finally(() => {
				this.saving = null;
				this.setBusy(false);
			});
		await this.saving;
		if (this.dirty.size && !this.destroyed) this.scheduleSave();
	}

	setBusy(busy) {
		this.root?.classList.toggle("is-saving", busy);
		if (this.refs.saveState) {
			this.refs.saveState.textContent = busy ? __("Saving...") : "";
		}
	}

	// -------------------------------------------------------------------------------
	// Actions
	// -------------------------------------------------------------------------------

	async goto(date) {
		if (!date) return;
		await this.flush();
		const sameWeek = this.days().includes(date);
		this.anchor = date;
		if (this.isMobile) this.selectedDay = null;
		if (sameWeek && this.payload) {
			this.render();
		} else {
			this.resetWeek();
			await this.load();
		}
		this.view?.update_url_with_filters?.();
	}

	shiftWeek(weeks) {
		return this.goto(
			moment(this.anchor, "YYYY-MM-DD")
				.add(weeks * 7, "days")
				.format("YYYY-MM-DD")
		);
	}

	async setEmployee(employee) {
		if (!employee || employee === this.employee) return;
		await this.flush();
		this.employee = employee;
		viewAsAnotherEmployee(employee); // the calendar layout / Add Entry follow it
		this.resetWeek();
		await this.load();
	}

	/** Forget the displayed week (before loading another week or employee). */
	resetWeek() {
		this.loadId++; // ignore a load still running for the previous week
		this.dirty.clear();
		this.invalid.clear();
		this.extraRows.clear();
		this.rows = [];
		this.payload = null;
		this.render();
	}

	fillFromAssignments() {
		const days = this.days();
		const candidates = [];
		for (const row of this.rows) {
			days.forEach((day, i) => {
				if (
					day <= today() &&
					row.planned[i] > 0 &&
					!this.effective(row, i) &&
					!this.isLocked(row, i)
				) {
					candidates.push({ row, i, minutes: row.planned[i] });
				}
			});
		}
		const { cells, reducedDays } = this.capCellsOnLeave(candidates);
		if (!cells.length) {
			frappe.show_alert({
				message: reducedDays
					? __("Nothing to fill: the assigned days left are on leave", null, "PM View")
					: __("Nothing to fill: every assigned day is already logged", null, "PM View"),
				indicator: "blue",
			});
			return;
		}
		const total = formatDuration(sum(cells.map((c) => c.minutes)));
		let question = __(
			"Log {0} on {1} day(s) from your assignments up to today?",
			[total, cells.length],
			"PM View"
		);
		if (reducedDays) {
			question += `<br>${__(
				"{0} assigned day(s) reduced or skipped for an approved leave.",
				[reducedDays],
				"PM View"
			)}`;
		}
		return new Promise((resolve) => {
			frappe.confirm(
				question,
				async () => {
					for (const cell of cells) {
						const key = cellKey(cell.row, cell.i);
						this.dirty.set(key, cell);
						const ref = this.refs.cells.get(key);
						if (ref) {
							ref.input.value = cellText(cell.minutes);
							this.paintCell(ref);
						}
					}
					this.updateTotals();
					await this.flush();
					resolve();
				},
				resolve
			);
		});
	}

	/**
	 * @dokos: assignments are not re-planned when a leave is approved afterwards. On a leave day,
	 * cap the filled time to the capacity left (time already logged included), shared in
	 * proportion to the plan. Other days (weekends, holidays, planned overtime) are kept as is.
	 */
	capCellsOnLeave(candidates) {
		const capacity = this.payload?.capacity || [];
		const leave = this.payload?.leave || [];
		const cells = [];
		let reducedDays = 0;
		this.days().forEach((_, i) => {
			const dayCells = candidates.filter((c) => c.i === i);
			if (!leave[i] || !dayCells.length) {
				cells.push(...dayCells);
				return;
			}
			const planned = sum(dayCells.map((c) => c.minutes));
			const logged = sum(this.rows.map((r) => this.effective(r, i)));
			const left = Math.max(0, Math.min(planned, (capacity[i] || 0) - logged));
			if (left === planned) {
				cells.push(...dayCells);
				return;
			}
			reducedDays++;
			const shares = dayCells.map((c) => Math.floor((c.minutes * left) / planned));
			// give the rounding remainder to the largest plan, so the day adds up to `left`
			const largest = dayCells.reduce(
				(best, c, k) => (c.minutes > dayCells[best].minutes ? k : best),
				0
			);
			shares[largest] += left - sum(shares);
			dayCells.forEach((cell, k) => {
				if (shares[k] > 0) cells.push({ ...cell, minutes: shares[k] });
			});
		});
		return { cells, reducedDays };
	}

	submitWeek() {
		return this.confirmWeekAction(
			__("Submit the timesheets of this week? Their time logs will be locked.", null, "PM View"),
			"submit_week",
			__("Timesheet submitted", null, "PM View")
		);
	}

	cancelWeek() {
		return this.confirmWeekAction(
			__("Cancel the submitted timesheets of this week?", null, "PM View"),
			"cancel_week",
			__("Timesheet cancelled", null, "PM View")
		);
	}

	confirmWeekAction(message, method, done) {
		return new Promise((resolve) => {
			frappe.confirm(
				message,
				async () => {
					try {
						await this.flush();
						await frappe.xcall(`${API}.${method}`, {
							employee: this.payload.employee,
							week_start: this.payload.week_start,
						});
						frappe.show_alert({ message: done, indicator: "green" });
					} catch (e) {
						frappe.show_alert({
							message: __("The action failed", null, "PM View"),
							indicator: "red",
						});
					}
					await this.load();
					resolve();
				},
				resolve
			);
		});
	}

	openAddRow() {
		const dialog = new frappe.ui.Dialog({
			title: __("Add a row", null, "PM View"),
			fields: [
				{
					fieldtype: "Link",
					fieldname: "project",
					options: "Project",
					label: __("Project"),
					get_query: () => ({ filters: { status: "Open" } }),
					onchange: () => {
						const task = dialog.get_value("task");
						const project = dialog.get_value("project");
						if (task && project && dialog._task_project && dialog._task_project !== project) {
							dialog.set_value("task", "");
						}
					},
				},
				{
					fieldtype: "Link",
					fieldname: "task",
					options: "Task",
					label: __("Task"),
					get_query: () => {
						const project = dialog.get_value("project");
						return { filters: project ? { project } : {} };
					},
					onchange: async () => {
						const task = dialog.get_value("task");
						if (!task) return;
						const { message } = await frappe.db.get_value("Task", task, "project");
						dialog._task_project = message?.project || null;
						if (dialog._task_project && !dialog.get_value("project")) {
							dialog.set_value("project", dialog._task_project);
						}
					},
				},
				{
					fieldtype: "Link",
					fieldname: "activity_type",
					options: "Activity Type",
					label: __("Activity Type"),
					get_query: () => ({ filters: { disabled: 0 } }),
				},
			],
			primary_action_label: __("Add"),
			primary_action: async (values) => {
				if (!values.project && !values.task && !values.activity_type) {
					frappe.msgprint(__("Select a project, a task or an activity type", null, "PM View"));
					return;
				}
				const key = [values.project || "", values.task || "", values.activity_type || ""].join("|");
				const existing = this.rows.find((r) => r.key === key);
				dialog.hide();
				if (existing) {
					frappe.show_alert({
						message: __("This row is already in the week", null, "PM View"),
						indicator: "orange",
					});
					this.focusRow(existing);
					return;
				}
				const [project, task] = await Promise.all([
					values.project ? frappe.db.get_value("Project", values.project, "project_name") : null,
					values.task ? frappe.db.get_value("Task", values.task, "subject") : null,
				]);
				const row = emptyRow({
					...values,
					project_name: project?.message?.project_name,
					task_subject: task?.message?.subject,
				});
				this.extraRows.set(row.key, row);
				this.rows = [...this.rows, row];
				this.render();
				this.focusRow(row);
			},
		});
		dialog.show();
	}

	focusRow(row) {
		const days = this.days();
		const preferred = this.isMobile ? this.currentDayIndex() : Math.max(0, days.indexOf(today()));
		const order = [preferred, ...days.map((_, i) => i).filter((i) => i !== preferred)];
		for (const i of order) {
			const input = this.refs.cells.get(cellKey(row, i))?.input;
			if (input && !input.disabled) {
				input.focus();
				return;
			}
		}
	}

	// -------------------------------------------------------------------------------
	// Cell editing
	// -------------------------------------------------------------------------------

	/** Validate the typed text; valid → dirty + debounced save. */
	onCellInput(ref) {
		const { row, i, input } = ref;
		const key = cellKey(row, i);
		const minutes = parseDuration(input.value);
		if (Number.isNaN(minutes) || minutes > MAX_MINUTES) {
			this.invalid.set(key, input.value);
			this.dirty.delete(key);
		} else {
			this.invalid.delete(key);
			if (minutes === (row.logged[i] || 0)) this.dirty.delete(key);
			else this.dirty.set(key, { row, i, minutes });
			this.scheduleSave();
		}
		this.paintCell(ref);
		this.updateTotals();
	}

	/** Set a cell from a button (stepper, quick buttons). */
	setCellMinutes(ref, minutes) {
		ref.input.value = cellText(Math.max(0, Math.min(MAX_MINUTES, minutes)));
		this.onCellInput(ref);
	}

	revertCell(ref) {
		const key = cellKey(ref.row, ref.i);
		this.dirty.delete(key);
		this.invalid.delete(key);
		ref.input.value = cellText(ref.row.logged[ref.i]);
		this.paintCell(ref);
		this.updateTotals();
	}

	onCellBlur(ref) {
		const key = cellKey(ref.row, ref.i);
		if (this.invalid.has(key)) return;
		// normalize "1.5" → "1h30"
		ref.input.value = cellText(this.effective(ref.row, ref.i));
	}

	onCellKeydown(event, ref) {
		switch (event.key) {
			case "Enter":
				event.preventDefault();
				this.flush();
				if (!this.isMobile) this.moveFocus(ref, 1);
				break;
			case "Escape":
				event.preventDefault();
				this.revertCell(ref);
				break;
			case "ArrowDown":
			case "ArrowUp":
				if (this.isMobile) return;
				event.preventDefault();
				this.moveFocus(ref, event.key === "ArrowDown" ? 1 : -1);
				break;
		}
	}

	/** Focus the same day of the next/previous editable row. */
	moveFocus(ref, direction) {
		let index = this.rows.findIndex((r) => r.key === ref.row.key);
		while (index >= 0 && index < this.rows.length) {
			index += direction;
			const input = this.rows[index] && this.refs.cells.get(cellKey(this.rows[index], ref.i))?.input;
			if (input && !input.disabled) {
				input.focus();
				input.select();
				return;
			}
		}
	}

	paintCell(ref) {
		const { row, i, input } = ref;
		const key = cellKey(row, i);
		const invalid = this.invalid.has(key);
		const submitted = row.docstatus?.[i] === 1;
		const locked = this.isLocked(row, i);
		input.disabled = locked;
		input.classList.toggle("is-invalid", invalid);
		input.classList.toggle("is-submitted", submitted && !invalid);
		input.classList.toggle("is-draft", !submitted && !invalid && input.value.trim() !== "");
		input.classList.toggle("is-empty", !submitted && !invalid && input.value.trim() === "");
		input.setAttribute("aria-invalid", invalid ? "true" : "false");
		input.title = invalid
			? __("Enter a duration like 1h30, 1:30, 1.5 or 90m (24h at most)", null, "PM View")
			: submitted
			? __("Submitted")
			: "";
	}

	// -------------------------------------------------------------------------------
	// Rendering
	// -------------------------------------------------------------------------------

	render() {
		if (!this.root) return;
		const focus = this.captureFocus();
		this.refs = this.emptyRefs();
		this.employeeControl = null;
		this.root.classList.toggle("pm-log--mobile", this.isMobile);
		this.root.replaceChildren(this.renderToolbar(), this.renderBody());
		// @dokos: the day chips scroll on a phone: keep the selected day in sight
		const chips = this.root.querySelector(".pm-log-chips");
		scrollIntoStrip(chips, chips?.querySelector('[aria-pressed="true"]'));
		this.restoreFocus(focus);
	}

	captureFocus() {
		const active = document.activeElement;
		if (!active?.dataset?.cell || !this.root?.contains(active)) return null;
		return {
			cell: active.dataset.cell,
			value: active.value,
			start: active.selectionStart,
			end: active.selectionEnd,
		};
	}

	restoreFocus(focus) {
		if (!focus) return;
		const ref = this.refs.cells.get(focus.cell);
		if (!ref || ref.input.disabled) return;
		if (this.dirty.has(focus.cell) || this.invalid.has(focus.cell)) ref.input.value = focus.value;
		ref.input.focus();
		try {
			ref.input.setSelectionRange(focus.start, focus.end);
		} catch (e) {
			// not a text input selection
		}
	}

	/** Update values in place (keeps the focused input and its caret untouched). */
	patch() {
		const active = document.activeElement;
		for (const [key, ref] of this.refs.cells) {
			const row = this.rows.find((r) => r.key === ref.row.key);
			if (!row) continue;
			ref.row = row;
			if (ref.input !== active && !this.dirty.has(key) && !this.invalid.has(key)) {
				ref.input.value = cellText(row.logged[ref.i]);
			} else if (this.dirty.has(key)) {
				this.dirty.get(key).row = row;
			}
			ref.input.placeholder = cellText(row.planned[ref.i]);
			this.paintCell(ref);
		}
		this.updateStatus();
		this.updateTotals();
	}

	renderToolbar() {
		const days = this.days();
		const bar = h("div", { class: "pm-log-toolbar" });

		// @dokos: es-buttons (façade): icon-only arrows named by their title
		const nav = h(
			"div",
			{ class: "pm-log-nav" },
			erpnext.dokos.ui.button({
				icon: "chevron-left",
				title: __("Previous week", null, "PM View"),
				onclick: () => this.shiftWeek(-1),
			})[0],
			erpnext.dokos.ui.button({
				icon: "chevron-right",
				title: __("Next week", null, "PM View"),
				onclick: () => this.shiftWeek(1),
			})[0],
			erpnext.dokos.ui.button({ label: __("Today"), onclick: () => this.goto(today()) })[0],
			h("h3", { class: "pm-log-week-label", text: weekLabel(days), attrs: { "aria-live": "polite" } })
		);
		bar.append(nav);

		const side = h("div", { class: "pm-log-toolbar-side" });
		side.append(this.renderEmployee());
		this.refs.status = h("span", { class: "pm-log-status" });
		side.append(this.refs.status);
		this.refs.saveState = h("span", {
			class: "pm-log-save-state dk-muted",
			attrs: { role: "status", "aria-live": "polite" },
		});
		side.append(this.refs.saveState);
		bar.append(side);
		this.updateStatus();
		return bar;
	}

	renderEmployee() {
		const wrapper = h("div", { class: "pm-log-employee" });
		if (!this.isManager()) {
			const name = this.payload?.employee_name || this.payload?.employee || "";
			wrapper.append(
				h("span", { class: "dk-muted", text: __("Employee") + ": " }),
				h("span", { class: "pm-log-employee-name", text: name })
			);
			return wrapper;
		}
		const control = frappe.ui.form.make_control({
			parent: wrapper,
			df: {
				fieldtype: "Link",
				fieldname: "pm_log_employee",
				options: "Employee",
				placeholder: __("Employee"),
				get_query: () => ({ filters: { status: "Active" } }),
				change: () => {
					if (this.employeeControl !== control) return;
					const value = control.get_value();
					if (value && value !== this.employee) this.setEmployee(value);
				},
			},
			only_input: true,
			render_input: true,
		});
		control.$input?.attr("aria-label", __("Employee"));
		const value = this.payload?.employee || this.employee;
		if (value) control.set_value(value);
		this.employeeControl = control;
		return wrapper;
	}

	updateStatus() {
		const el = this.refs.status;
		if (!el) return;
		el.replaceChildren();
		const status = this.payload?.status;
		if (!status) return;
		const label = status === "Empty" ? __("Not started", null, "PM View") : __(status);
		el.append(
			h("span", { class: "dk-muted", text: __("Timesheet") }),
			erpnext.dokos.ui.pill({
				label,
				theme: STATUS_THEME[status] || erpnext.dokos.ui.status_theme(status),
			})[0] // @dokos
		);
	}

	renderBody() {
		// @dokos: façade empty state / skeleton instead of a muted paragraph
		if (this.error) {
			return erpnext.dokos.ui.empty_state({
				icon: "user-x",
				title: __("The week could not be loaded", null, "PM View"),
				description: __("Check that your user is linked to an active employee.", null, "PM View"),
				css_class: "pm-log-empty",
			})[0];
		}
		if (!this.payload) {
			return h(
				"div",
				{ class: "pm-log-empty", attrs: { "aria-busy": "true" } },
				erpnext.dokos.ui.skeleton({ width: "100%", height: "160px" })[0],
				h("span", { class: "sr-only", text: __("Loading...") })
			);
		}
		return this.isMobile ? this.renderMobile() : this.renderGrid();
	}

	/** @dokos: empty week (façade empty state); the add-a-row action sits right under it. */
	renderEmptyWeek() {
		return erpnext.dokos.ui.empty_state({
			icon: "clock",
			title: __("No assignment nor time logged this week", null, "PM View"),
			description: __("Add a row to log time.", null, "PM View"),
			css_class: "pm-empty-week",
		})[0];
	}

	renderActivityTag(activityType) {
		if (!activityType) return null;
		return h("span", {
			class: "pm-log-tag",
			text: __(activityType),
			style: { "--pm-color": activityColor(activityType, this.colors[activityType]) },
		});
	}

	renderRowLabel(row) {
		const label = h("div", { class: "pm-log-row-label" });
		if (row.project) {
			label.append(
				h("div", { class: "pm-log-project ellipsis", text: row.project_name, title: row.project })
			);
		}
		if (row.task) {
			label.append(
				h("a", {
					class: "pm-log-task ellipsis",
					href: frappe.utils.get_form_link("Task", row.task),
					text: row.task_subject || row.task,
					title: row.task,
				})
			);
		} else if (!row.project) {
			label.append(
				h("div", { class: "pm-log-task dk-muted", text: __("No project", null, "PM View") })
			);
		}
		const meta = h("div", { class: "pm-log-row-meta" }, this.renderActivityTag(row.activity_type));
		if (!sum(row.planned)) {
			meta.append(h("span", { class: "pm-log-unplanned", text: __("no assignment", null, "PM View") }));
		}
		label.append(meta);
		return label;
	}

	rowTitle(row) {
		return row.task_subject || row.project_name || __(row.activity_type || "No project");
	}

	/** One cell input (shared by the grid and the mobile cards). */
	renderInput(row, i, dayLabel) {
		const key = cellKey(row, i);
		const planned = row.planned[i] || 0;
		const input = h("input", {
			type: "text",
			class: "pm-log-input",
			value: this.invalid.has(key) ? this.invalid.get(key) : cellText(this.effective(row, i)),
			placeholder: cellText(planned),
			autocomplete: "off",
			attrs: {
				inputmode: "decimal",
				"data-cell": key,
				"aria-label": [
					this.rowTitle(row),
					dayLabel,
					planned ? __("assigned {0}", [formatDuration(planned)], "PM View") : null,
				]
					.filter(Boolean)
					.join(", "),
			},
		});
		const ref = { row, i, input };
		input.addEventListener("input", () => this.onCellInput(ref));
		input.addEventListener("keydown", (e) => this.onCellKeydown(e, ref));
		input.addEventListener("blur", () => this.onCellBlur(ref));
		input.addEventListener("focus", () => input.select());
		this.refs.cells.set(key, ref);
		this.paintCell(ref);
		return ref;
	}

	renderPlannedBadge(minutes) {
		return h("span", {
			class: ["pm-log-planned", !minutes && "is-none"],
			text: minutes ? formatDuration(minutes) : "",
			title: minutes ? __("Assigned") : "",
			attrs: { "aria-hidden": "true" },
		});
	}

	renderGrid() {
		const days = this.days();
		const todayStr = today();
		const colClass = (day) => [
			"pm-log-day",
			isWeekend(day) && "is-weekend",
			day === todayStr && "is-today",
		];

		const table = h("table", { class: "pm-log-grid" });
		table.append(
			h(
				"caption",
				{ class: "sr-only" },
				__("Time logged by day for the week of {0}", [weekLabel(days)], "PM View")
			)
		);

		// Header
		const headRow = h(
			"tr",
			{},
			h("th", {
				scope: "col",
				class: "pm-log-label-col",
				text: __("Project · Task · Activity Type", null, "PM View"),
			})
		);
		for (const day of days) {
			headRow.append(
				h(
					"th",
					{
						scope: "col",
						class: colClass(day),
						attrs: { "aria-label": formatDay(day, "fullDay") },
					},
					h("span", { class: "pm-log-dow", text: formatDay(day, "weekday") }),
					h("span", { class: "pm-log-daynum", text: String(Number(day.slice(8, 10))) })
				)
			);
		}
		headRow.append(h("th", { scope: "col", class: "pm-log-total-col", text: __("Total") }));
		table.append(h("thead", {}, headRow));

		// Rows
		const body = h("tbody");
		for (const row of this.rows) {
			const tr = h(
				"tr",
				{},
				h("th", { scope: "row", class: "pm-log-label-col" }, this.renderRowLabel(row))
			);
			days.forEach((day, i) => {
				const dayLabel = formatDay(day, "longDay");
				const ref = this.renderInput(row, i, dayLabel);
				tr.append(
					h(
						"td",
						{ class: colClass(day) },
						h("div", { class: "pm-log-cell" }, this.renderPlannedBadge(row.planned[i]), ref.input)
					)
				);
			});
			const total = h("td", { class: "pm-log-total-col" });
			this.refs.rowTotals.set(row.key, total);
			tr.append(total);
			body.append(tr);
		}
		if (!this.rows.length) {
			body.append(
				h(
					"tr",
					{},
					h("td", { class: "pm-log-empty", colSpan: days.length + 2 }, this.renderEmptyWeek())
				)
			);
		}
		table.append(body);

		// Footer: add a row + day totals
		const foot = h("tfoot");
		if (this.canWrite() && this.payload.status !== "Submitted") {
			foot.append(
				h(
					"tr",
					{ class: "pm-log-add-row" },
					h(
						"td",
						{ colSpan: days.length + 2 },
						erpnext.dokos.ui.button({
							label: __("Add a row: project, task, activity type", null, "PM View"),
							icon: "plus",
							variant: "ghost",
							css_class: "pm-log-add-btn",
							onclick: () => this.openAddRow(),
						})[0]
					)
				)
			);
		}
		const totals = h(
			"tr",
			{ class: "pm-log-day-totals" },
			h(
				"th",
				{ scope: "row", class: "pm-log-label-col" },
				h("div", { class: "pm-log-totals-title", text: __("Day total", null, "PM View") }),
				h("div", {
					class: "pm-log-totals-help dk-muted",
					text: __("Logged / working hours", null, "PM View"),
				})
			)
		);
		days.forEach((day) => {
			const label = h("span", { class: "pm-log-day-total" });
			const fill = h("span", { class: "pm-bar-fill" });
			const bar = h("span", { class: "pm-bar", attrs: { "aria-hidden": "true" } }, fill);
			this.refs.dayTotals.push({ label, fill, bar });
			totals.append(
				h("td", { class: colClass(day) }, h("div", { class: "pm-log-day-total-cell" }, label, bar))
			);
		});
		this.refs.week = h("td", { class: "pm-log-total-col pm-log-week-total" });
		totals.append(this.refs.week);
		foot.append(totals);
		table.append(foot);

		this.updateTotals();
		return h(
			"div",
			{ class: "pm-log-body" },
			h("div", { class: "pm-log-scroll" }, table),
			h("p", {
				class: "pm-log-hint dk-muted",
				text: __(
					"Type 1h30, 1:30, 1.5 or 90m. Each cell is one time log on your weekly Timesheet; Enter or ↑/↓ move between rows, Esc reverts.",
					null,
					"PM View"
				),
			})
		);
	}

	currentDayIndex() {
		const days = this.days();
		if (this.selectedDay != null && days[this.selectedDay]) return this.selectedDay;
		let index = days.indexOf(this.anchor);
		if (index < 0) index = days.indexOf(today());
		if (index < 0)
			index = Math.max(
				0,
				(this.payload?.capacity || []).findIndex((c) => c > 0)
			);
		this.selectedDay = index;
		return index;
	}

	selectDay(index) {
		this.selectedDay = index;
		this.anchor = this.days()[index];
		this.render();
		this.view?.update_url_with_filters?.();
		this.refs.chips[index]?.button.focus();
	}

	renderMobile() {
		const days = this.days();
		const dayIndex = this.currentDayIndex();
		const wrapper = h("div", { class: "pm-log-body pm-log-mobile" });

		const chips = h("div", {
			class: "pm-log-chips",
			attrs: { role: "group", "aria-label": __("Day", null, "PM View") },
		});
		days.forEach((day, i) => {
			const total = h("span", { class: "pm-log-chip-total" });
			const button = h(
				"button",
				{
					type: "button",
					class: ["pm-log-chip", isWeekend(day) && "is-weekend", day === today() && "is-today"],
					attrs: { "aria-pressed": i === dayIndex ? "true" : "false" },
					on: { click: () => this.selectDay(i) },
				},
				h("span", { class: "pm-log-chip-day", text: formatDay(day, "weekdayDay") }),
				total
			);
			this.refs.chips.push({ button, total });
			chips.append(button);
		});
		wrapper.append(chips);

		const dayLabel = formatDay(days[dayIndex], "longDay");
		const cards = h("div", { class: "pm-log-cards" });
		for (const row of this.rows) {
			const planned = row.planned[dayIndex] || 0;
			const ref = this.renderInput(row, dayIndex, dayLabel);
			const locked = this.isLocked(row, dayIndex);
			// @dokos: es-buttons; the visible text is a symbol, the name says what it does
			const stepBtn = (text, label, delta, css_class = "") =>
				erpnext.dokos.ui.button({
					label: text,
					disabled: locked,
					css_class,
					attrs: { "aria-label": label },
					onclick: () => this.setCellMinutes(ref, this.effective(ref.row, ref.i) + delta),
				})[0];
			const quick = h(
				"div",
				{ class: "pm-log-quick" },
				stepBtn("+30m", __("Add 30 minutes", null, "PM View"), 30),
				stepBtn("+1h", __("Add 1 hour", null, "PM View"), 60),
				planned
					? erpnext.dokos.ui.button({
							label: __("= assigned", null, "PM View"),
							disabled: locked,
							attrs: {
								"aria-label": __(
									"Log the assigned {0}",
									[formatDuration(planned)],
									"PM View"
								),
							},
							onclick: () => this.setCellMinutes(ref, planned),
					  })[0]
					: null
			);
			cards.append(
				h(
					"div",
					{ class: "pm-log-card" },
					h(
						"div",
						{ class: "pm-log-card-head" },
						this.renderRowLabel(row),
						this.renderPlannedBadge(planned)
					),
					h(
						"div",
						{ class: "pm-log-stepper" },
						stepBtn("−", __("Remove 15 minutes", null, "PM View"), -STEP, "pm-log-step"),
						ref.input,
						stepBtn("+", __("Add 15 minutes", null, "PM View"), STEP, "pm-log-step")
					),
					quick
				)
			);
		}
		if (!this.rows.length) {
			cards.append(h("div", { class: "pm-log-empty" }, this.renderEmptyWeek()));
		}
		if (this.canWrite() && this.payload.status !== "Submitted") {
			cards.append(
				erpnext.dokos.ui.button({
					label: __("Log time on another task", null, "PM View"),
					icon: "plus",
					css_class: "pm-log-add-btn",
					onclick: () => this.openAddRow(),
				})[0]
			);
		}
		wrapper.append(cards);

		this.refs.week = h("div", { class: "pm-log-week-total" });
		const footer = h(
			"div",
			{ class: "pm-log-mobile-footer" },
			h(
				"div",
				{},
				h("div", { class: "dk-muted", text: __("This week", null, "PM View") }),
				this.refs.week
			)
		);
		if (this.canSubmit()) {
			footer.append(
				erpnext.dokos.ui.button({
					label: __("Submit Timesheet"),
					variant: "solid",
					onclick: () => this.submitWeek(),
				})[0]
			);
		}
		wrapper.append(footer);
		this.updateTotals();
		return wrapper;
	}

	/** Row, day and week totals from the effective (saved or edited) minutes. */
	updateTotals() {
		if (!this.payload) return;
		const days = this.days();
		const capacity = this.payload.capacity || [];
		const leave = this.payload.leave || [];
		const dayLogged = days.map((_, i) => sum(this.rows.map((r) => this.effective(r, i))));
		const weekPlanned = sum(this.rows.map((r) => sum(r.planned)));
		const weekLogged = sum(dayLogged);

		for (const row of this.rows) {
			const el = this.refs.rowTotals.get(row.key);
			if (!el) continue;
			const logged = sum(days.map((_, i) => this.effective(row, i)));
			const planned = sum(row.planned);
			// @dokos: native replaceChildren() would render a null child as the text "null"
			const children = [
				h("div", { class: "pm-log-row-total", text: logged ? formatDuration(logged) : "—" }),
			];
			if (planned) {
				children.push(
					h("div", {
						class: "pm-log-row-planned dk-muted",
						text: __("of {0}", [formatDuration(planned)], "PM View"),
					})
				);
			}
			el.replaceChildren(...children);
		}

		this.refs.dayTotals.forEach(({ label, fill, bar }, i) => {
			const logged = dayLogged[i];
			const cap = capacity[i] || 0;
			label.textContent = `${logged ? formatDuration(logged) : "—"}${
				cap ? " / " + formatDuration(cap) : ""
			}`;
			// @dokos: time logged on a full leave day (capacity 0) is over too
			const over = logged > cap && (cap || leave[i]);
			label.classList.toggle("is-over", Boolean(over));
			fill.style.width = cap ? `${Math.min(100, Math.round((logged / cap) * 100))}%` : "0";
			bar.classList.toggle("pm-level--over", Boolean(over));
			bar.classList.toggle("pm-level--full", Boolean(cap && !over && logged >= cap - 30));
		});

		this.refs.chips.forEach(({ total }, i) => {
			const logged = dayLogged[i];
			const cap = capacity[i] || 0;
			total.textContent =
				cap && logged >= cap
					? `${formatDuration(logged)} ✓`
					: `${logged ? formatDuration(logged) : "—"}${
							cap ? " " + __("of {0}", [formatDuration(cap)], "PM View") : ""
					  }`;
		});

		if (this.refs.week) {
			this.refs.week.replaceChildren(
				h("span", { class: "pm-log-week-logged", text: formatDuration(weekLogged) }),
				h("span", {
					class: "dk-muted",
					text: " " + __("of {0} assigned", [formatDuration(weekPlanned)], "PM View"),
				})
			);
		}
	}
}
